import React, { useEffect, useRef, useState } from "react";
import { Box, getColor, Modal, Typography, useToast } from "fds";
import "./index.scss";
import { t } from "i18next";
import { Trans, useSSR } from "react-i18next";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { useNavigate } from "react-router-dom";
import { nanoid } from "@reduxjs/toolkit";
import utilsCommon from "../../shared/utils/common";
import {
  getRedirectUrl,
  getUserInfoFromLocalStorage,
  removeRedirectUrl,
} from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";

interface ESETLoginModalProps {
  open: boolean;
  userId: string;
  isAdmin?: boolean;
  changeLoading: (status: boolean) => void;
  onClose: () => void;
}
const ESETLoginModal: React.FC<ESETLoginModalProps> = ({
  open,
  userId,
  isAdmin,
  onClose,
  changeLoading,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const navigate = useNavigate();
  const toasts = useToast();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { startEsetLogin, pollingEsetLogin, sessionLogout } = useLoginStore();
  const [requestId, changeRequestId] = useState("");
  useEffect(() => {
    startEsetLogin(userId).then((item: any) => {
      changeRequestId(item.payload.pushChallengeId);
    });
  }, []);

  useEffect(() => {
    if (!requestId || requestId === "") return;
    let isActive = true;
    const poll = async () => {
      try {
        const res = await pollingEsetLogin({
          pushChallengeId: requestId,
          userId: userId,
        });

        if (res.payload.apiToken) {
          let redirectUrl = getRedirectUrl();
          if (redirectUrl && !redirectUrl.includes("/login")) {
            removeRedirectUrl();
            try {
              const url = new URL(redirectUrl);
              navigate(url.pathname);
            } catch (err) {
              window.location.replace(redirectUrl);
            }
          } else {
            const defaultTenancy = res.payload?.defaultTenancy ?? "My Space";
            const featureAccessType =
              res.payload?.ecoSystemLicenseStats?.featureAccessType ?? "";

            navigate(
              isAdmin
                ? `${process.env.PUBLIC_URL}/admin/home`
                : featureAccessType === "LITE"
                ? `${process.env.PUBLIC_URL}/myInbox/received`
                : window.eco.serviceType === "OCI" && defaultTenancy
                ? `${process.env.PUBLIC_URL}/${defaultTenancy}/home`
                : `${process.env.PUBLIC_URL}/home`
            );
          }
          isActive = false;
          timeoutRef.current && clearTimeout(timeoutRef.current);
          return;
        }

        if (isActive) {
          intervalRef.current = setTimeout(poll, 2000);
        }
      } catch (err) {
        toasts.toastMsg(nanoid(), t("login.esetOTP.fail"), "error");
        isActive = false;
        timeoutRef.current && clearTimeout(timeoutRef.current);
        sessionLogout();
        onClose();
      }
    };

    poll();

    return () => {
      isActive = false;
      clearTimeout(intervalRef.current!);
    };
  }, [requestId]);
  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      toasts.toastMsg(nanoid(), t("login.esetOTP.fail"), "error");
      clearInterval(intervalRef.current!);
      sessionLogout();
      onClose();
    }, 300 * 1000);
    return () => clearTimeout(timeoutRef.current!);
  }, []);
  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        sessionLogout();
      }}
      title={t("login.esetOTP.title")}
      width={600}
      bodyHeight={366}
      classes="esetLoginModal"
      isFooter={false}
      body={
        <Box direction="column" height={"100%"}>
          <Typography
            type="body2_rg"
            whiteSpace="break-spaces"
            exactColor={getColor("COLOR_TEXT_SECONDARY")}
          >
            {t("login.esetOTP.desc")}
          </Typography>
          <Box classes="mg mt-18" direction="column">
            <Box classes="esetLoginModal-step">
              <img src={require("../../shared/images/pages/login/eset1.png")} />
              <Typography
                classes="mg ml-40 stepCircle"
                type="h3"
                exactColor="white"
              >
                1
              </Typography>
              <Box style={{ marginInlineStart: "17px" }} width={320}>
                <Typography type="body2_rg">
                  <Trans
                    i18nKey={"login.esetOTP.step1"}
                    values={{ app: "ESET Secure Authentication" }}
                    components={{
                      1: <Typography type="subtitle2" />,
                    }}
                  />
                </Typography>
              </Box>
            </Box>
            <Box classes="esetLoginModal-step mg mt-12">
              <img src={require("../../shared/images/pages/login/eset2.png")} />
              <Typography
                classes="mg ml-40 stepCircle"
                type="h3"
                exactColor="white"
              >
                2
              </Typography>
              <Box style={{ marginInlineStart: "17px" }} width={320}>
                <Typography type="body2_rg">
                  {t("login.esetOTP.step2")}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      }
    />
  );
};
export default ESETLoginModal;
